import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import AnalyseIA from './AnalyseIA';
import { lignesDuBloc, totauxDuBloc, blocsAnalysables } from '../utils/lignesDuDashboard';
import { normaliserSitesDeMesure } from '../utils/sitesDeMesure';
import { AlertCircle, EyeOff, Users, TrendingUp } from 'lucide-react';
import MatomoPerformanceChart from './MatomoPerformanceChart';
import MatomoFunnelChart from './MatomoFunnelChart';
import MatomoTransitionsChart from './MatomoTransitionsChart';
import DashboardLoadingScreen from './DashboardLoadingScreen';
import HiddenModulesModal from './HiddenModulesModal';
import { useClientModuleVisibility } from '../hooks/useClientModuleVisibility';
import matomoLogo from '../logos/matomo.png';
import { dateLocale, jourIso } from '../utils/periodUtils';

/**
 * Agrège les données quotidiennes selon la granularité
 * (réplique de la logique backend pour éviter les rechargements)
 */
function aggregateByGranularity(dailyData, granularity) {
  if (!dailyData || dailyData.length === 0) return [];

  if (granularity === 'day') {
    return dailyData;
  }

  // Pour les autres granularités, grouper par période
  const grouped = {};

  dailyData.forEach(day => {
    const periodKey = getPeriodKey(day.date, granularity);

    if (!grouped[periodKey]) {
      grouped[periodKey] = {
        date: periodKey,
        visits: 0,
        uniqueVisitors: 0,
        pageviews: 0,
        conversions: 0,
        revenue: 0,
        bounceSum: 0,
        timeSum: 0,
        actionsSum: 0,
        dayCount: 0
      };
    }

    grouped[periodKey].visits += day.visits || 0;
    grouped[periodKey].uniqueVisitors += day.uniqueVisitors || 0;
    grouped[periodKey].pageviews += day.pageviews || 0;
    grouped[periodKey].conversions += day.conversions || 0;
    grouped[periodKey].revenue += day.revenue || 0;
    grouped[periodKey].bounceSum += (day.bounceRate || 0) * (day.visits || 0);
    grouped[periodKey].timeSum += (day.avgTimeOnSite || 0) * (day.visits || 0);
    grouped[periodKey].actionsSum += (day.actionsPerVisit || 0) * (day.visits || 0);
    grouped[periodKey].dayCount++;
  });

  // Calculer les moyennes
  return Object.values(grouped).map(period => ({
    date: period.date,
    visits: period.visits,
    uniqueVisitors: period.uniqueVisitors,
    pageviews: period.pageviews,
    conversions: period.conversions,
    revenue: period.revenue,
    bounceRate: period.visits > 0
      ? (period.bounceSum / period.visits)
      : 0,
    avgTimeOnSite: period.visits > 0
      ? (period.timeSum / period.visits)
      : 0,
    actionsPerVisit: period.visits > 0
      ? (period.actionsSum / period.visits)
      : 0
  })).sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Retourne la clé de période pour une date
 */
function getPeriodKey(dateStr, granularity) {
  // Minuit local, et non minuit UTC : voir `dateLocale`. Le repère doit être le même à la
  // lecture et à l'écriture, sans quoi le lundi d'une semaine saute d'un jour.
  const date = dateLocale(dateStr);

  switch (granularity) {
    case 'week':
      // Trouver le lundi de la semaine
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(date);
      monday.setDate(diff);
      return jourIso(monday);

    case 'month':
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

    case 'quarter':
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `${date.getFullYear()}-Q${quarter}`;

    case 'year':
      return `${date.getFullYear()}`;

    default:
      return dateStr;
  }
}

/**
 * MatomoDashboardView - Vue dashboard pour Matomo Analytics
 *
 * Affiche:
 * - Modal de sélection de site (si aucun site sélectionné)
 * - Composant Résultats (MatomoPerformanceChart) - style identique à Google Ads
 */

const MatomoDashboardView = ({
  /**
   * L'analyse IA est-elle ouverte à cet utilisateur ?
   *
   * Un membre d'organisation est jugé sur son plan, un accès client sur la permission
   * `analyse-ia` accordée dans Organisation › Accès clients. La règle est calculée une fois
   * dans `App` et descendue ici : `autorise` valant `true` par défaut, ne rien passer montrait
   * le bouton à un client que le serveur refuse ensuite.
   */
  analyseAutorisee = false,
  // Notes à reporter sur le graphique. Traversent la vue sans y être lues.
  dashboardNotes = [],
  token,
  selectedPeriod,
  customStartDate,
  customEndDate,
  matomoSites,
  filteredMatomoSites = [],
  setMatomoSites,
  matomoSelectedSiteId,
  setMatomoSelectedSiteId,
  matomoDashboardData,
  setMatomoDashboardData,
  matomoLoading,
  setMatomoLoading,
  matomoGranularity,
  setMatomoGranularity,
  KPICard,
  formatNumber,
  // Props pour la configuration des métriques
  accountId = 'global',
  userRole = 'USER',
  // Props pour la comparaison
  compareEnabled = false,
  comparisonType = 'previous_period'
}) => {
  const [error, setError] = useState(null);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [waitingForData, setWaitingForData] = useState(true); // Attendre les données avant d'afficher

  // Calculer le clientId effectif pour la visibilité des modules
  const effectiveClientId = useMemo(() => {
    return matomoSelectedSiteId ? `matomo_${matomoSelectedSiteId}` : null;
  }, [matomoSelectedSiteId]);

  // Hook pour la visibilité des modules par client
  const {
    isModuleVisible: isClientModuleVisible,
    toggleModuleVisibility: toggleClientModuleVisibility,
    setModuleVisibility: setClientModuleVisibility,
    hiddenModules: clientHiddenModules
  } = useClientModuleVisibility(effectiveClientId, userRole);

  // State pour le modal des modules masqués
  const [isHiddenModulesModalOpen, setIsHiddenModulesModalOpen] = useState(false);

  // Ref pour suivre si on a chargé des données fraîches dans cette session
  const hasFetchedFreshDataRef = useRef(false);

  // Charger la liste des sites Matomo au montage
  useEffect(() => {
    const fetchMatomoSites = async () => {
      if (!token) return;

      setSitesLoading(true);
      setWaitingForData(true); // Reset au montage
      try {
        const response = await fetch('/api/dashboard/matomo/sites', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();

        if (data.success && data.sites) {
          // Même forme canonique que celle posée par `App.js` sur la charge de `/api/library` :
          // les deux sources écrivent dans le même état, et un désaccord sur le sens de `id`
          // faisait basculer la sélection, les clés React et le paramètre envoyé à l'API.
          setMatomoSites(normaliserSitesDeMesure(data.sites));
        } else {
          setError(data.error || 'Erreur lors du chargement des sites Matomo');
          setWaitingForData(false);
        }
      } catch (err) {
        console.error('Erreur fetch sites Matomo:', err);
        setError('Impossible de charger les sites Matomo');
        setWaitingForData(false);
      } finally {
        setSitesLoading(false);
      }
    };

    fetchMatomoSites();
  }, [token, setMatomoSites]);

  // Auto-sélectionner si un seul site Matomo filtré disponible
  useEffect(() => {
    if (!sitesLoading && filteredMatomoSites.length === 1) {
      const currentSiteValid = matomoSelectedSiteId && filteredMatomoSites.find(s => s.id === matomoSelectedSiteId);
      if (!currentSiteValid) {
        console.log('🎯 [MATOMO] Auto-sélection du seul site:', filteredMatomoSites[0].name);
        setMatomoSelectedSiteId(filteredMatomoSites[0].id);
      }
    }
    // Si plusieurs sites ou aucun, on arrête d'attendre (afficher le sélecteur ou message)
    if (!sitesLoading && filteredMatomoSites.length !== 1) {
      setWaitingForData(false);
    }
  }, [sitesLoading, filteredMatomoSites, matomoSelectedSiteId, setMatomoSelectedSiteId]);

  // Arrêter d'attendre quand les données du dashboard sont chargées (après le chargement initial)
  useEffect(() => {
    // Ne pas déclencher si les sites ne sont pas encore chargés
    // ET s'assurer qu'on a bien chargé des données fraîches (pas des données en cache)
    if (!sitesLoading && matomoSelectedSiteId && matomoDashboardData && !matomoLoading && hasFetchedFreshDataRef.current) {
      setWaitingForData(false);
    }
  }, [sitesLoading, matomoSelectedSiteId, matomoDashboardData, matomoLoading]);

  // Charger les données du dashboard quand le site ou la période change
  // Note: On récupère toujours les données quotidiennes, l'agrégation se fait côté client
  const fetchDashboardData = useCallback(async () => {
    if (!token || !matomoSelectedSiteId) return;

    setMatomoLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/dashboard/matomo/timeline', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          siteId: matomoSelectedSiteId,
          period: selectedPeriod,
          granularity: 'day', // Toujours récupérer les données quotidiennes, agrégation côté client
          customStartDate,
          customEndDate,
          compareEnabled,
          comparisonType
        })
      });

      const data = await response.json();

      if (data.success) {
        setMatomoDashboardData(data);
        hasFetchedFreshDataRef.current = true; // Marquer que les données sont fraîches
      } else {
        setError(data.error || 'Erreur lors du chargement des données');
        setMatomoDashboardData(null);
      }
    } catch (err) {
      console.error('Erreur fetch dashboard Matomo:', err);
      setError('Impossible de charger les données du dashboard');
      setMatomoDashboardData(null);
    } finally {
      setMatomoLoading(false);
    }
  }, [token, matomoSelectedSiteId, selectedPeriod, customStartDate, customEndDate, compareEnabled, comparisonType, setMatomoLoading, setMatomoDashboardData]);

  // Charger les données quand les paramètres changent (sauf granularité qui est gérée côté client)
  useEffect(() => {
    if (matomoSelectedSiteId) {
      fetchDashboardData();
    }
  }, [matomoSelectedSiteId, selectedPeriod, customStartDate, customEndDate, compareEnabled, comparisonType, fetchDashboardData]);

  // Agréger les données quotidiennes selon la granularité sélectionnée (côté client)
  const aggregatedDailyData = useMemo(() => {
    const rawDaily = matomoDashboardData?.data?.daily || [];
    return aggregateByGranularity(rawDaily, matomoGranularity);
  }, [matomoDashboardData?.data?.daily, matomoGranularity]);

  // Handler pour le changement de site
  const handleSiteChange = (e) => {
    const newSiteId = e.target.value;
    if (newSiteId) {
      setMatomoSelectedSiteId(newSiteId);
      setMatomoDashboardData(null);
    }
  };

  // Handler pour le changement de granularité
  const handleGranularityChange = (newGranularity) => {
    setMatomoGranularity(newGranularity);
  };

  // Grouper les sites filtrés par instance Matomo
  const sitesByInstance = filteredMatomoSites.reduce((acc, site) => {
    const instanceKey = site.instanceUrl || 'Instance par défaut';
    if (!acc[instanceKey]) {
      acc[instanceKey] = [];
    }
    acc[instanceKey].push(site);
    return acc;
  }, {});

  // Cas 1: Chargement en cours (sites OU données OU attente auto-sélection)
  if (sitesLoading || matomoLoading || waitingForData) {
    return (
      <DashboardLoadingScreen
        platformName="Matomo"
        features={[
          { icon: <Users size={28} />, label: 'Visiteurs et sessions' },
          { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
          { icon: <img src={matomoLogo} alt="Matomo" style={{width: '28px', height: '28px', borderRadius: '4px'}} />, label: 'Analyse du trafic' }
        ]}
      />
    );
  }

  // Cas 2: Aucun site Matomo configuré du tout
  if (matomoSites.length === 0) {
    return (
      <div className="account-selection-prompt">
        <div className="account-selection-card">
          <div className="account-selection-icon">
            <span style={{ fontSize: '48px' }}>📊</span>
          </div>
          <h2>Aucun site Matomo configuré</h2>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>
            Connectez une instance Matomo dans "Mes Connecteurs" pour commencer.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => window.dispatchEvent(new CustomEvent('navigateTo', { detail: { view: 'connectors' } }))}
            style={{ fontSize: '16px', padding: '12px 24px' }}
          >
            + Ajouter un connecteur
          </button>
        </div>
      </div>
    );
  }

  // Cas 2b: Des sites existent mais aucun ne correspond au filtre client/site
  if (filteredMatomoSites.length === 0) {
    return (
      <div className="account-selection-prompt">
        <div className="account-selection-card">
          <div className="account-selection-icon">
            <span style={{ fontSize: '48px' }}>🔍</span>
          </div>
          <h2>Aucun site Matomo associé</h2>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>
            Aucun site Matomo n'est associé au client ou site sélectionné.<br />
            Associez des comptes via "Gestion des sites" pour les voir apparaître ici.
          </p>
        </div>
      </div>
    );
  }

  // Cas 3: Sites disponibles mais aucun sélectionné - Afficher le modal de sélection
  if (!matomoSelectedSiteId || !filteredMatomoSites.find(s => s.id === matomoSelectedSiteId)) {
    return (
      <div className="account-selection-prompt">
        <div className="account-selection-card">
          <div className="account-selection-icon">
            <img
              src={matomoLogo}
              alt="Matomo"
              style={{ width: '48px', height: '48px', borderRadius: '8px' }}
            />
          </div>
          <h2>Sélectionnez un site Matomo</h2>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>
            {filteredMatomoSites.length} site{filteredMatomoSites.length > 1 ? 's' : ''} disponible{filteredMatomoSites.length > 1 ? 's' : ''}
          </p>
          <select
            className="account-selection-dropdown"
            value=""
            onChange={handleSiteChange}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '16px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Choisir un site...</option>
            {Object.keys(sitesByInstance).length > 1 ? (
              Object.entries(sitesByInstance).map(([instanceUrl, sites]) => (
                <optgroup key={instanceUrl} label={instanceUrl}>
                  {sites.sort((a, b) => a.name.localeCompare(b.name)).map(site => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </optgroup>
              ))
            ) : (
              [...filteredMatomoSites].sort((a, b) => a.name.localeCompare(b.name)).map(site => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))
            )}
          </select>
        </div>
      </div>
    );
  }

  // Cas 4: Site sélectionné mais aucune donnée
  const summary = matomoDashboardData?.data?.summary;
  const hasData = summary && (
    (summary.totalVisits || 0) > 0 ||
    (summary.visits || 0) > 0 ||
    (summary.uniqueVisitors || 0) > 0 ||
    (summary.totalPageviews || 0) > 0 ||
    (summary.pageviews || 0) > 0
  );

  if (!hasData && matomoDashboardData && !matomoLoading) {
    return (
      <div className="matomo-dashboard-view">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 24px',
          backgroundColor: '#FAF9F5',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <img
            src={matomoLogo}
            alt="Matomo"
            style={{ width: '48px', height: '48px', borderRadius: '8px', opacity: 0.5, marginBottom: '16px' }}
          />
          <h3 style={{ color: '#64748b', fontWeight: '500', marginBottom: '8px' }}>
            Aucune donnée disponible pour cette période
          </h3>
          <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
            Essayez de sélectionner une période différente ou vérifiez que le tracking Matomo est actif.
          </p>
        </div>
      </div>
    );
  }

  // Cas 5: Site sélectionné avec données - Afficher le dashboard
  /**
   * Ce qu'on envoie à l'analyse : les données **déjà affichées**.
   *
   * Appelée à l'envoi de la question et non à l'ouverture du panneau : les blocs arrivent en
   * plusieurs temps, et une charge figée trop tôt capture un écran incomplet.
   */
  const construireChargeAnalyse = () => {
    const periode = {
      period: selectedPeriod,
      ...(selectedPeriod === 'CUSTOM' && { customStartDate, customEndDate })
    };

    /*
     * **Les blocs vivent sous `data`, et les parcourir un cran trop haut n'en rendait qu'un.**
     *
     * `matomoDashboardData` vaut `{ success, data: { summary, daily, channels, geography, devices, … } }`.
     * En bouclant sur le premier niveau, la seule entrée exploitable était `data` tout entier, et
     * `lignesDuBloc` — qui ne rend qu'**un** tableau, le plus grand — y choisissait la
     * géographie. L'analyse ne voyait donc que les continents et concluait, à juste titre, qu'il
     * n'y avait rien d'autre à dire.
     *
     * Le repli sur l'objet lui-même garde l'écran GA4 en état : là, les blocs sont au premier
     * niveau.
     */
    const sourceDonnees = blocsAnalysables(matomoDashboardData);
    const blocs = {};
    const absents = [];
    // Le détail de cet écran vit sous des clés qui diffèrent d'une version à l'autre : on
    // parcourt ce qu'il y a, plutôt que d'en nommer une liste qui vieillirait mal.
    for (const [libelle, bloc] of Object.entries(sourceDonnees)) {
      if (!bloc || typeof bloc !== 'object') continue;
      const reduit = lignesDuBloc(bloc);
      if (reduit) { blocs[libelle] = reduit; continue; }
      // Un bloc sans lignes porte souvent les chiffres de tête — `currentPeriod` et consorts.
      const totaux = totauxDuBloc(bloc);
      if (totaux) blocs[libelle] = { totaux };
      else absents.push(libelle);
    }

    const site = (filteredMatomoSites || []).find((s) => String(s.id ?? s.idsite ?? s.idSite) === String(matomoSelectedSiteId));

    return {
      dashboardData: {
        sources: [matomoSelectedSiteId].filter(Boolean),
        ...periode,
        charts: sourceDonnees.charts,
        composants: { blocs, absents },
        dashboardType: 'matomo'
      },
      dashboardType: 'matomo',
      accountId: matomoSelectedSiteId || null,
      accountName: site?.name || site?.displayName || null,
      ...periode
    };
  };

  return (
    <div className="matomo-dashboard-view">
      {/*
        L'analyse IA. Ce dashboard n'a pas de barre de filtres : le bouton occupe sa propre
        ligne, alignée à droite comme les autres actions d'écran. Le composant rend aussi le
        panneau, qui passe par un portail — sa place dans l'arbre n'a aucune incidence.
      */}
      {matomoSelectedSiteId && !matomoLoading && matomoDashboardData && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <AnalyseIA
            token={token}
            autorise={analyseAutorisee}
            compte={{ id: matomoSelectedSiteId }}
            construireCharge={construireChargeAnalyse}
          />
        </div>
      )}

      {/* Barre avec lien Modules masqués */}
      {userRole !== 'CLIENT' && clientHiddenModules.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '16px'
        }}>
          <div
            className="hidden-modules-link"
            onClick={() => setIsHiddenModulesModalOpen(true)}
            title="Voir les modules masqués pour le client"
          >
            <EyeOff size={16} />
            <span>Modules masqués</span>
            <span className="hidden-count">{clientHiddenModules.length}</span>
          </div>
        </div>
      )}

      {/* Modal des modules masqués */}
      <HiddenModulesModal
        isOpen={isHiddenModulesModalOpen}
        onClose={() => setIsHiddenModulesModalOpen(false)}
        hiddenModules={clientHiddenModules}
        onRestoreModule={(moduleId) => setClientModuleVisibility(moduleId, true)}
      />

      {/* Message d'erreur */}
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          marginBottom: '16px',
          color: '#b91c1c'
        }}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Graphique de performance Matomo - même style que Google Ads */}
      {(userRole !== 'CLIENT' || isClientModuleVisible('matomo_performance')) && (
        <MatomoPerformanceChart
          notes={dashboardNotes}
          data={aggregatedDailyData}
          summary={matomoDashboardData?.data?.summary}
          previousPeriodData={matomoDashboardData?.previousPeriod}
          channels={matomoDashboardData?.data?.channels || []}
          geography={matomoDashboardData?.data?.geography || []}
          devices={matomoDashboardData?.data?.devices || []}
          browsers={matomoDashboardData?.data?.browsers || []}
          languages={matomoDashboardData?.data?.languages || []}
          landingPages={matomoDashboardData?.data?.landingPages || []}
          weekdays={matomoDashboardData?.data?.weekdays || []}
          ecommerceItems={matomoDashboardData?.data?.ecommerceItems || []}
          events={matomoDashboardData?.data?.events || []}
          granularity={matomoGranularity}
          onGranularityChange={handleGranularityChange}
          loading={matomoLoading}
          KPICard={KPICard}
          formatNumber={formatNumber}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="matomo_performance"
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          token={token}
          siteId={matomoSelectedSiteId}
          period={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
        />
      )}

      {/* Entonnoir de conversion */}
      {(userRole !== 'CLIENT' || isClientModuleVisible('matomo_funnel')) && (
        <MatomoFunnelChart
          events={matomoDashboardData?.data?.events || []}
          formatNumber={formatNumber}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="matomo_funnel"
        />
      )}

      {/* Transitions de pages */}
      {(userRole !== 'CLIENT' || isClientModuleVisible('matomo_transitions')) && (
        <MatomoTransitionsChart
          token={token}
          siteId={matomoSelectedSiteId}
          period={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          formatNumber={formatNumber}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="matomo_transitions"
        />
      )}
    </div>
  );
};

export default MatomoDashboardView;
